/**
 * <video> element 의 listener 와 src 를 정리해 메모리 잔존을 막는다.
 *
 * 대용량 mp4 메타데이터 추출 후 GC 가 비디오 디코더 자원을 회수할 수 있도록 강제로
 * 디코더 종료를 트리거한다. listener 미정리 시 video element 가 closure 에 살아남아
 * 다수 영상 순차 처리에서 메모리 누적이 일어난다.
 *
 * 동작:
 * - `onloadedmetadata`, `onerror` 참조 해제 (closure leak 방지)
 * - `removeAttribute('src')` + `load()`: 디코더 종료 트리거
 * - `blobUrl` 인자 제공 시 `URL.revokeObjectURL` 동시 호출
 *
 * 호출자가 `URL.createObjectURL` 결과를 외부에서 별도 관리한다면 `blobUrl` 을 생략하고
 * 호출자가 직접 revoke 한다.
 *
 * **사용 제한**: 일회성 video element 만 사용. `video.load()` 호출 시 빈 src 로
 * NotSupportedError 가 발생할 수 있는데, 본 helper 는 `onerror=null` 을 먼저 처리하므로
 * silent. 그러나 같은 video element 를 재사용 (retry 등) 하는 시나리오에서는 stale error
 * 가 다음 라이프사이클에 영향. 재사용 케이스는 file.ts 의 `destroyVideo` 패턴 (load() 미호출) 사용.
 *
 * 사용:
 * ```ts
 * const video = document.createElement('video');
 * const url = URL.createObjectURL(blob);
 * const cleanup = () => releaseVideoElement(video, url);
 * video.onloadedmetadata = () => { cleanup(); resolve(...); };
 * video.onerror = () => { cleanup(); reject(...); };
 * video.src = url;
 * ```
 */
export const releaseVideoElement = (
  video: HTMLVideoElement,
  blobUrl?: string,
): void => {
  video.onloadedmetadata = null;
  video.onerror = null;
  video.removeAttribute('src');
  video.load();
  if (blobUrl != null) {
    URL.revokeObjectURL(blobUrl);
  }
};
